// ============================================
// COMPONENT: CalibrationHistoryView
// ============================================
// Archivo: src/components/calibration/CalibrationHistoryView.jsx

import React, { useState, useMemo } from 'react';
import {
  ArrowLeft,
  Download,
  Filter,
  CheckCircle,
  AlertTriangle,
  Clock,
  Eye,
  LogOut,
  XCircle,
  UserCog
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import * as XLSX from 'xlsx';

const CalibrationHistoryView = ({ 
  calibrations = [],
  plants = [],
  equipments = [],
  user,
  onBack,
  onLogout,
  onOpenMyData,
  onViewCalibration,
  getCalibrationStatus
}) => {
  const [filters, setFilters] = useState({
    plantId: '',
    status: '', // valid, warning, expired, no-data
    metodo: '',
    startDate: '',
    endDate: '',
    searchTerm: ''
  });

  const [showFilters, setShowFilters] = useState(false);

  // Calcular estado de cada calibración
  const calibrationsWithStatus = useMemo(() => {
    return calibrations.map(cal => {
      const equipment = equipments.find(e => e.id === cal.equipment_id);
      const plant = plants.find(p => p.id === equipment?.plantId);
      
      // Calcular estado
      let status = 'no-data';
      let daysUntilExpiry = null;
      
      if (cal.proxima_calibracion) {
        const today = new Date();
        const nextDate = new Date(cal.proxima_calibracion);
        daysUntilExpiry = Math.floor((nextDate - today) / (1000 * 60 * 60 * 24));
        
        if (daysUntilExpiry < 0) {
          status = 'expired';
        } else if (daysUntilExpiry <= 30) {
          status = 'warning';
        } else {
          status = 'valid';
        }
      }

      return {
        ...cal,
        equipment_name: equipment?.name || 'Desconocido',
        equipment_code: equipment?.code || '',
        plant_name: plant?.name || 'Desconocida',
        plant_code: plant?.code || '',
        status,
        daysUntilExpiry
      };
    });
  }, [calibrations, equipments, plants]);

  // Filtrar calibraciones
  const filteredCalibrations = useMemo(() => {
    return calibrationsWithStatus.filter(cal => {
      // Filtro por planta
      if (filters.plantId) {
        const equipment = equipments.find(e => e.id === cal.equipment_id);
        if (equipment?.plantId !== parseInt(filters.plantId)) {
          return false;
        }
      }

      // Filtro por estado
      if (filters.status && cal.status !== filters.status) {
        return false;
      }

      // Filtro por método
      if (filters.metodo && cal.metodo_calibracion !== filters.metodo) {
        return false;
      }

      // Filtro por fecha inicio
      if (filters.startDate && cal.fecha_calibracion < filters.startDate) {
        return false;
      }

      // Filtro por fecha fin
      if (filters.endDate && cal.fecha_calibracion > filters.endDate) {
        return false;
      }

      // Búsqueda por texto
      if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        const matchEquipment = cal.equipment_name?.toLowerCase().includes(term);
        const matchCode = cal.equipment_code?.toLowerCase().includes(term);
        const matchPlant = cal.plant_name?.toLowerCase().includes(term);
        const matchResultado = cal.resultado?.toLowerCase().includes(term);
        return matchEquipment || matchCode || matchPlant || matchResultado;
      }

      return true;
    });
  }, [calibrationsWithStatus, filters, equipments]);

  // Estadísticas
  const stats = useMemo(() => {
    const total = filteredCalibrations.length;
    const vigentes = filteredCalibrations.filter(c => c.status === 'valid').length;
    const porVencer = filteredCalibrations.filter(c => c.status === 'warning').length;
    const vencidas = filteredCalibrations.filter(c => c.status === 'expired').length;
    
    const equiposUnicos = new Set(filteredCalibrations.map(c => c.equipment_id)).size;

    return {
      total,
      vigentes,
      porVencer,
      vencidas,
      equiposUnicos
    };
  }, [filteredCalibrations]);

  // Distribución de estados
  const chartStatusDist = useMemo(() => [
    { name: 'Vigentes',   value: stats.vigentes,  fill: '#00e676' },
    { name: 'Por Vencer', value: stats.porVencer, fill: '#ffb300' },
    { name: 'Vencidas',   value: stats.vencidas,  fill: '#ff3b5c' },
  ].filter(d => d.value > 0), [stats]);

  // Calibraciones por mes (últimos 6 meses)
  const chartCalByMonth = useMemo(() => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      months.push({
        mes: d.toLocaleDateString('es-CL', { month: 'short', year: '2-digit' }),
        year: d.getFullYear(),
        month: d.getMonth(),
        cantidad: 0
      });
    }
    filteredCalibrations.forEach(c => {
      const d = new Date(c.fecha_calibracion);
      const m = months.find(mo => mo.year === d.getFullYear() && mo.month === d.getMonth());
      if (m) m.cantidad++;
    });
    return months;
  }, [filteredCalibrations]);

  // Top 5 equipos con más calibraciones vencidas
  const chartExpiredByEquipment = useMemo(() => {
    const counts = {};
    filteredCalibrations
      .filter(c => c.status === 'expired')
      .forEach(c => {
        const name = c.equipment_name || 'Desconocido';
        counts[name] = (counts[name] || 0) + 1;
      });
    return Object.entries(counts)
      .map(([equipo, cantidad]) => ({ equipo, cantidad }))
      .sort((a, b) => b.cantidad - a.cantidad)
      .slice(0, 5);
  }, [filteredCalibrations]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      plantId: '',
      status: '',
      metodo: '',
      startDate: '',
      endDate: '',
      searchTerm: ''
    });
  };

  const exportToExcel = () => {
    if (filteredCalibrations.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    // Preparar datos para Excel
    const data = filteredCalibrations.map(cal => ({
      'ID': cal.id,
      'Fecha Calibración': new Date(cal.fecha_calibracion).toLocaleDateString('es-CL'),
      'Próxima Calibración': cal.proxima_calibracion ? new Date(cal.proxima_calibracion).toLocaleDateString('es-CL') : '',
      'Días Para Vencer': cal.daysUntilExpiry !== null ? cal.daysUntilExpiry : 'N/A',
      'Estado': getStatusLabel(cal.status),
      'Planta': cal.plant_name,
      'Código Planta': cal.plant_code,
      'Equipo': cal.equipment_name,
      'Código Equipo': cal.equipment_code,
      'Tipo Calibración': cal.tipo_calibracion || '',
      'Método': cal.metodo_calibracion || '',
      'Resultado': cal.resultado || '',
      'Observaciones': cal.observaciones || '',
      'Responsable': cal.responsable || '',
      'Certificado N°': cal.numero_certificado || '',
      'Organismo': cal.organismo_certificador || '',
      'Creado': cal.created_at ? new Date(cal.created_at).toLocaleDateString('es-CL') : '',
      'Actualizado': cal.updated_at ? new Date(cal.updated_at).toLocaleDateString('es-CL') : ''
    }));

    // Crear worksheet
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Ajustar ancho de columnas
    const colWidths = [
      { wch: 5 },  // ID
      { wch: 15 }, // Fecha Calibración
      { wch: 18 }, // Próxima Calibración
      { wch: 15 }, // Días Para Vencer
      { wch: 12 }, // Estado
      { wch: 20 }, // Planta
      { wch: 15 }, // Código Planta
      { wch: 25 }, // Equipo
      { wch: 15 }, // Código Equipo
      { wch: 20 }, // Tipo Calibración
      { wch: 25 }, // Método
      { wch: 15 }, // Resultado
      { wch: 40 }, // Observaciones
      { wch: 20 }, // Responsable
      { wch: 20 }, // Certificado N°
      { wch: 25 }, // Organismo
      { wch: 15 }, // Creado
      { wch: 15 }  // Actualizado
    ];
    ws['!cols'] = colWidths;

    // Crear workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Historial Calibraciones');

    // Generar archivo
    const filename = `Historial_Calibraciones_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'valid': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'expired': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'valid': return 'Vigente';
      case 'warning': return 'Por Vencer';
      case 'expired': return 'Vencida';
      default: return 'Sin Datos';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid': return <CheckCircle size={16} className="text-green-500" />;
      case 'warning': return <AlertTriangle size={16} className="text-yellow-500" />;
      case 'expired': return <XCircle size={16} className="text-red-500" />;
      default: return <Clock size={16} className="text-gray-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Navigation */}
      <nav className="bg-slate-800 px-6 py-4 border-b border-slate-700">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-6">
            <button
              onClick={onBack}
              className="text-cyan-400 hover:text-cyan-300"
            >
              <ArrowLeft size={24} />
            </button>
            <CheckCircle size={32} className="text-green-400" />
            <div className="text-white">
              <h1 className="text-xl font-bold">Historial de Calibraciones</h1>
              <p className="text-xs text-slate-400">
                Sistema de Gestión de Calibraciones PRT
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <p className="text-sm text-cyan-400">
              {new Date().toLocaleString('es-CL')}
            </p>
            {onOpenMyData && (
              <button onClick={onOpenMyData} title="Mis Datos" className="p-2 text-slate-300 hover:text-white flex items-center gap-1 text-sm">
                <UserCog size={15} /><span className="hidden sm:inline">Mis Datos</span>
              </button>
            )}
            <button
              onClick={onLogout}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-2"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="p-6">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-5 gap-4 mb-6">
          <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
            <p className="text-slate-400 text-sm mb-1">Total Calibraciones</p>
            <p className="text-3xl font-bold text-white">{stats.total}</p>
          </div>
          
          <div className="bg-slate-800 p-4 rounded-lg border border-green-500">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle size={16} className="text-green-500" />
              <p className="text-slate-400 text-sm">Vigentes</p>
            </div>
            <p className="text-3xl font-bold text-green-500">{stats.vigentes}</p>
          </div>
          
          <div className="bg-slate-800 p-4 rounded-lg border border-yellow-500">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle size={16} className="text-yellow-500" />
              <p className="text-slate-400 text-sm">Por Vencer (≤30d)</p>
            </div>
            <p className="text-3xl font-bold text-yellow-500">{stats.porVencer}</p>
          </div>
          
          <div className="bg-slate-800 p-4 rounded-lg border border-red-500">
            <div className="flex items-center gap-2 mb-1">
              <XCircle size={16} className="text-red-500" />
              <p className="text-slate-400 text-sm">Vencidas</p>
            </div>
            <p className="text-3xl font-bold text-red-500">{stats.vencidas}</p>
          </div>
          
          <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
            <p className="text-slate-400 text-sm mb-1">Equipos Únicos</p>
            <p className="text-3xl font-bold text-white">{stats.equiposUnicos}</p>
          </div>
        </div>

        {/* Gráficos */}
        {filteredCalibrations.length > 0 && (
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {/* Estado de calibraciones */}
            <div className="bg-[#0d1826] p-4 rounded-lg border border-slate-700">
              <h3 className="text-white text-sm font-semibold mb-3">Estado de Calibraciones</h3>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={chartStatusDist}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="45%"
                    outerRadius={60}
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {chartStatusDist.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', color: '#fff' }} />
                  <Legend wrapperStyle={{ fontSize: 10, color: '#94a3b8' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Calibraciones por mes */}
            <div className="bg-[#0d1826] p-4 rounded-lg border border-slate-700">
              <h3 className="text-white text-sm font-semibold mb-3">Calibraciones por Mes</h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartCalByMonth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
                  <XAxis dataKey="mes" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', color: '#fff' }} />
                  <Bar dataKey="cantidad" fill="#00d4ff" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Top 5 equipos vencidos */}
            <div className="bg-[#0d1826] p-4 rounded-lg border border-slate-700">
              <h3 className="text-white text-sm font-semibold mb-3">Equipos con Más Vencidas</h3>
              {chartExpiredByEquipment.length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={chartExpiredByEquipment} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
                    <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="equipo" tick={{ fill: '#94a3b8', fontSize: 10 }} width={80} />
                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', color: '#fff' }} />
                    <Bar dataKey="cantidad" fill="#ff3b5c" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[180px]">
                  <p className="text-slate-500 text-sm">Sin calibraciones vencidas</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="bg-slate-800 p-4 rounded-lg border border-slate-700 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-white font-semibold">Filtros y Acciones</h2>
            <div className="flex gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-4 py-2 bg-slate-700 text-white rounded hover:bg-slate-600 flex items-center gap-2"
              >
                <Filter size={16} />
                {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
              </button>
              <button
                onClick={exportToExcel}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2"
              >
                <Download size={16} />
                Exportar a Excel
              </button>
            </div>
          </div>

          {/* Filtros */}
          {showFilters && (
            <div className="grid md:grid-cols-4 gap-4 pt-4 border-t border-slate-700">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Planta</label>
                <select
                  value={filters.plantId}
                  onChange={(e) => handleFilterChange('plantId', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                >
                  <option value="">Todas las plantas</option>
                  {plants.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Estado</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                >
                  <option value="">Todos</option>
                  <option value="valid">Vigente</option>
                  <option value="warning">Por Vencer (≤30 días)</option>
                  <option value="expired">Vencida</option>
                  <option value="no-data">Sin Datos</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Método</label>
                <select
                  value={filters.metodo}
                  onChange={(e) => handleFilterChange('metodo', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                >
                  <option value="">Todos</option>
                  <option value="interno">Interno</option>
                  <option value="externo">Externo</option>
                  <option value="fabricante">Fabricante</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Buscar</label>
                <input
                  type="text"
                  value={filters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  placeholder="Equipo, código, resultado..."
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm placeholder-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Fecha Desde</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Fecha Hasta</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                />
              </div>

              <div className="md:col-span-2 flex items-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-slate-700 text-slate-300 rounded hover:bg-slate-600 text-sm"
                >
                  Limpiar Filtros
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Tabla de Calibraciones */}
        <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
          {filteredCalibrations.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle size={48} className="mx-auto text-slate-600 mb-4" />
              <p className="text-slate-400">No se encontraron calibraciones</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Planta</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Equipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Código</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Fecha Cal.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Próxima Cal.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Días Restantes</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Método</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Resultado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Responsable</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {filteredCalibrations.map(cal => (
                    <tr key={cal.id} className="hover:bg-slate-750">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(cal.status)}
                          <span className={`px-2 py-1 rounded text-xs font-semibold text-white ${getStatusColor(cal.status)}`}>
                            {getStatusLabel(cal.status)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-white font-medium">
                        {cal.plant_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {cal.equipment_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-400">
                        {cal.equipment_code}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {new Date(cal.fecha_calibracion).toLocaleDateString('es-CL')}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {cal.proxima_calibracion 
                          ? new Date(cal.proxima_calibracion).toLocaleDateString('es-CL')
                          : 'N/A'
                        }
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {cal.daysUntilExpiry !== null ? (
                          <span className={`font-semibold ${
                            cal.daysUntilExpiry < 0 ? 'text-red-500' :
                            cal.daysUntilExpiry <= 30 ? 'text-yellow-500' :
                            'text-green-500'
                          }`}>
                            {cal.daysUntilExpiry} días
                          </span>
                        ) : (
                          <span className="text-slate-500">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {cal.tipo_calibracion || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {cal.metodo_calibracion || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`font-semibold ${
                          cal.resultado === 'conforme' ? 'text-green-500' :
                          cal.resultado === 'no_conforme' ? 'text-red-500' :
                          'text-slate-400'
                        }`}>
                          {cal.resultado === 'conforme' ? 'Conforme' :
                           cal.resultado === 'no_conforme' ? 'No Conforme' :
                           'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {cal.responsable || 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => onViewCalibration(cal)}
                          className="p-2 text-blue-400 hover:text-blue-300"
                          title="Ver detalles"
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info de resultados */}
        <div className="mt-4 text-center text-sm text-slate-400">
          Mostrando {filteredCalibrations.length} de {calibrations.length} calibraciones
        </div>
      </div>
    </div>
  );
};

export default CalibrationHistoryView;
